import { fmt, fmtDate } from '@dcc-erp/shared';
import type { BankReconAdjustmentType, BankStatementWithChildren } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { lineMovement, num } from './recon-helpers';

const ADJ_LABELS: Readonly<Record<BankReconAdjustmentType, string>> = {
  BANK_FEE: 'ค่าธรรมเนียมธนาคาร',
  INTEREST: 'ดอกเบี้ยรับ',
  FX: 'ผลต่างอัตราแลกเปลี่ยน',
  CHECK_NOT_CLEARED: 'เช็คยังไม่ขึ้นเงิน',
  DEPOSIT_IN_TRANSIT: 'เงินฝากระหว่างทาง',
  OTHER: 'อื่นๆ',
};

export function ReconStatement({
  statement,
}: {
  statement: BankStatementWithChildren | undefined;
}) {
  if (!statement) {
    return (
      <div className="card">
        <div className="card-body muted" style={{ padding: 40, textAlign: 'center' }}>
          ยังไม่ได้เลือก statement — เลือกบัญชีที่นำเข้าแล้วจากแท็บ "ภาพรวมทุกบัญชี"
        </div>
      </div>
    );
  }
  const opening = num(statement.openingBalance);
  const closing = num(statement.closingBalance);
  const movement = lineMovement(statement.lines);
  const reconciles = Math.round((opening + movement) * 100) === Math.round(closing * 100);
  const matched = statement.lines.filter((l) => l.status === 'MATCHED').length;
  const open = statement.lines.filter((l) => l.status === 'OPEN').length;
  const adjusted = statement.lines.filter((l) => l.status === 'ADJUSTED').length;

  return (
    <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
      <div className="card-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <b>
            {statement.bankCode} {statement.bankAccNo} — {statement.bankAccountName}
          </b>
          <span className="badge brand">งวด {fmtDate(statement.statementDate)}</span>
          {statement.status === 'RECONCILED' ? (
            <span className="badge positive">
              <Icon name="check" size={10} /> Reconciled
            </span>
          ) : (
            <span className="badge warning">DRAFT</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm" disabled title="เฟสถัดไป">
            <Icon name="print" size={12} /> พิมพ์
          </button>
          <button className="btn sm" disabled title="เฟสถัดไป">
            <Icon name="download" size={12} /> Export PDF
          </button>
        </div>
      </div>

      <div
        style={{
          margin: 22,
          padding: 18,
          background: reconciles ? '#e7f4ec' : '#fce9e7',
          border: `2px solid ${reconciles ? 'var(--positive)' : 'var(--negative)'}`,
          borderRadius: 10,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr',
          gap: 14,
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div>
          <div className="meta">ยอดยกมา</div>
          <div className="tnum" style={{ fontSize: 20, fontWeight: 700 }}>
            {fmt(opening)}
          </div>
        </div>
        <div style={{ fontSize: 24, color: 'var(--text-faint)' }}>+</div>
        <div>
          <div className="meta">เคลื่อนไหว (รับ−จ่าย)</div>
          <div className="tnum" style={{ fontSize: 20, fontWeight: 700 }}>
            {fmt(movement)}
          </div>
        </div>
        <div style={{ fontSize: 24, color: 'var(--text-faint)' }}>=</div>
        <div>
          <div className="meta">ยอดปิด</div>
          <div className="tnum" style={{ fontSize: 20, fontWeight: 700 }}>
            {fmt(closing)}
          </div>
        </div>
        <div style={{ fontSize: 24, color: reconciles ? 'var(--positive)' : 'var(--negative)' }}>
          {reconciles ? '✓' : '✗'}
        </div>
        <div>
          <div className="meta">สถานะ</div>
          <div
            style={{
              fontWeight: 700,
              color: reconciles ? 'var(--positive)' : 'var(--negative)',
            }}
          >
            {reconciles ? 'ตรง' : 'ไม่ตรง'}
          </div>
        </div>
      </div>

      <div style={{ padding: '0 22px 18px' }}>
        <div className="section-title">รายการปรับปรุง ({statement.adjustments.length})</div>
        <table
          className="tbl"
          style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
        >
          <thead>
            <tr>
              <th>ประเภท</th>
              <th>คำอธิบาย</th>
              <th className="num">จำนวน</th>
              <th>JV</th>
            </tr>
          </thead>
          <tbody>
            {statement.adjustments.length === 0 && (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 20, textAlign: 'center' }}>
                  ยังไม่มีรายการปรับปรุง
                </td>
              </tr>
            )}
            {statement.adjustments.map((a) => (
              <tr key={a.id}>
                <td>{ADJ_LABELS[a.adjustmentType]}</td>
                <td>{a.description}</td>
                <td className={`num tnum ${num(a.amount) < 0 ? 'neg' : 'pos'}`}>
                  {fmt(num(a.amount))}
                </td>
                <td className="code muted">{a.journalEntryId ? 'ลง JV แล้ว' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          padding: '18px 22px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 24,
          fontSize: 14,
        }}
      >
        <div>
          <div className="meta">รายการ statement</div>
          <div style={{ marginTop: 6 }}>
            จับคู่แล้ว {matched} · ปรับปรุงแล้ว {adjusted} · ค้าง {open}
          </div>
        </div>
        <div>
          <div className="meta">จัดทำ/นำเข้าโดย</div>
          <div style={{ marginTop: 6 }}>
            <b>{statement.importedBy ?? '—'}</b>
          </div>
        </div>
        <div>
          <div className="meta">ปิดกระทบยอดโดย</div>
          <div style={{ marginTop: 6 }}>
            <b>{statement.finalizedBy ?? '—'}</b>
            {statement.finalizedAt ? (
              <div className="meta">{fmtDate(statement.finalizedAt)}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
